import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import AlphaThetaCanvas from './components/AlphaThetaCanvas';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FEEDBACK_MESSAGES = [
  'received by the field',
  'the inkwell ripples',
  'your blessing joins the standing wave',
  'the oscillation deepens',
  'carried into the collective',
  'the field brightens',
  'a new frequency enters',
  'the wave remembers',
];

export default function App() {
  const [blessingText, setBlessingText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  const [fieldCount, setFieldCount] = useState(0);
  const [recentBlessings, setRecentBlessings] = useState([]);

  const [pulse, setPulse] = useState('');
  const [isPulseVisible, setIsPulseVisible] = useState(false);
  const [isPulseLoading, setIsPulseLoading] = useState(false);
  const [showPulseTrigger, setShowPulseTrigger] = useState(false);

  // Refs for canvas communication
  const fieldStateRef = useRef({ count: 0, alpha: 1.0, theta: 0, R: 0.167 });
  const burstRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const inputRef = useRef(null);

  // Mouse/touch tracking
  useEffect(() => {
    const onMove = (e) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
    };
    const onTouch = (e) => {
      if (e.touches[0]) {
        mouseRef.current.x = e.touches[0].clientX / window.innerWidth;
        mouseRef.current.y = e.touches[0].clientY / window.innerHeight;
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onTouch, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
    };
  }, []);

  const updateFieldCount = (count) => {
    setFieldCount(count);
    if (count >= 3) setShowPulseTrigger(true);
  };

  const loadField = useCallback(async () => { // eslint-disable-line react-hooks/exhaustive-deps
    try {
      const res = await fetch(`${BACKEND_URL}/api/field`);
      const data = await res.json();
      if (data.alpha_theta) {
        fieldStateRef.current = {
          count: data.count,
          alpha: data.alpha_theta.alpha,
          theta: data.alpha_theta.theta,
          R: data.alpha_theta.R,
        };
      }
      updateFieldCount(data.count || 0);
      if (data.recent && data.recent.length > 0) {
        // Filter out test data (long timestamps) and truncate
        const clean = data.recent
          .filter(b => b && b.length < 100)
          .map(b => b.replace(/\*\*/g, '').trim())
          .slice(0, 3);
        setRecentBlessings(clean);
      }
      loadPulse();
    } catch (e) {}
  }, []);

  const loadPulse = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/pulse/latest`);
      const data = await res.json();
      if (data.pulse) {
        const cleanPulse = data.pulse.replace(/\*\*/g, '').replace(/\*/g, '').trim();
        setPulse(cleanPulse);
        setIsPulseVisible(true);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadField();
    const interval = setInterval(loadField, 30000);
    return () => clearInterval(interval);
  }, [loadField]);

  const sendBlessing = async () => {
    const text = blessingText.trim();
    if (!text || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/blessings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();

      if (data.alpha_theta) {
        fieldStateRef.current = {
          count: data.count,
          alpha: data.alpha_theta.alpha,
          theta: data.alpha_theta.theta,
          R: data.alpha_theta.R,
        };
      }

      // Fire burst + update energy in canvas
      if (burstRef.current) {
        burstRef.current(data.count || 0);
      }

      const msg = FEEDBACK_MESSAGES[Math.floor(Math.random() * FEEDBACK_MESSAGES.length)];
      setFeedbackMsg('');
      setTimeout(() => setFeedbackMsg(msg), 10);

      updateFieldCount(data.count || 0);
      setBlessingText('');

      // Refresh recent blessings
      setRecentBlessings((prev) => [text, ...prev].slice(0, 3));
    } catch (e) {
      setFeedbackMsg('');
      setTimeout(() => setFeedbackMsg('the field is still forming...'), 10);
    }

    setIsSubmitting(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') sendBlessing();
  };

  const generatePulse = async () => {
    if (isPulseLoading) return;
    setIsPulseLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/pulse`, { method: 'POST' });
      const data = await res.json();

      if (data.pulse) {
        const cleanPulse = data.pulse.replace(/\*\*/g, '').replace(/\*/g, '').trim();
        setPulse(cleanPulse);
        setIsPulseVisible(true);
        // Large burst on pulse
        if (burstRef.current) {
          burstRef.current(50);
        }
      } else if (data.message) {
        setFeedbackMsg('');
        setTimeout(() => setFeedbackMsg(data.message), 10);
      }
    } catch (e) {
      setFeedbackMsg('');
      setTimeout(() => setFeedbackMsg('the pulse will emerge when ready'), 10);
    }

    setIsPulseLoading(false);
  };

  const formatCount = (n) => {
    if (!n || n === 0) return 'the field awaits';
    if (n === 1) return 'one blessing in the inkwell';
    return `${n} blessings in the inkwell`;
  };

  return (
    <div className="supralumen-root">
      {/* Layer 0: Full-screen canvas */}
      <AlphaThetaCanvas
        fieldState={fieldStateRef}
        onBurst={burstRef}
        mouseRef={mouseRef}
      />

      {/* Layer 1: Blur */}
      <div className="blur-layer" aria-hidden="true" />

      {/* Layer 2: Frost */}
      <div className="frost-layer" aria-hidden="true" />

      {/* Layer 3: UI Overlay */}
      <div className="ui-layer">
        {/* Header */}
        <header className="header">
          {/* SVG Logotype: SUPRA ◇ LUMEN */}
          <svg
            className="logo-svg"
            viewBox="0 0 480 80"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="SupraLumen"
            data-testid="app-title"
          >
            <defs>
              <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8a6f2e" />
                <stop offset="25%" stopColor="#c9a84c" />
                <stop offset="50%" stopColor="#e8d06c" />
                <stop offset="75%" stopColor="#c9a84c" />
                <stop offset="100%" stopColor="#8a6f2e" />
              </linearGradient>
              <filter id="goldGlow">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Top decorative line */}
            <line x1="20" y1="10" x2="460" y2="10" stroke="url(#goldGrad)" strokeWidth="0.6" opacity="0.4" />

            {/* SUPRA text */}
            <text
              x="100"
              y="52"
              textAnchor="middle"
              fontFamily="'SupraLumen', 'Cormorant Garamond', Georgia, serif"
              fontSize="32"
              fontWeight="400"
              fill="url(#goldGrad)"
              filter="url(#goldGlow)"
              letterSpacing="6"
            >
              SUPRA
            </text>

            {/* Diamond separator */}
            <text
              x="240"
              y="52"
              textAnchor="middle"
              fontFamily="Georgia, serif"
              fontSize="18"
              fill="url(#goldGrad)"
              opacity="0.7"
            >
              ◇
            </text>

            {/* LUMEN text */}
            <text
              x="380"
              y="52"
              textAnchor="middle"
              fontFamily="'SupraLumen', 'Cormorant Garamond', Georgia, serif"
              fontSize="32"
              fontWeight="400"
              fill="url(#goldGrad)"
              filter="url(#goldGlow)"
              letterSpacing="6"
            >
              LUMEN
            </text>

            {/* Bottom decorative line */}
            <line x1="20" y1="63" x2="460" y2="63" stroke="url(#goldGrad)" strokeWidth="0.6" opacity="0.4" />

            {/* Circle ornament */}
            <circle cx="240" cy="73" r="2.5" fill="none" stroke="url(#goldGrad)" strokeWidth="0.6" opacity="0.35" />
            <line x1="220" y1="73" x2="232" y2="73" stroke="url(#goldGrad)" strokeWidth="0.4" opacity="0.25" />
            <line x1="248" y1="73" x2="260" y2="73" stroke="url(#goldGrad)" strokeWidth="0.4" opacity="0.25" />
          </svg>

          <p className="tagline" data-testid="app-subtitle">Belonging Beyond Light</p>
          <p className="field-count" data-testid="blessing-count">{formatCount(fieldCount)}</p>
        </header>

        {/* Center area */}
        <div className="center-area">
          {/* Pulse display */}
          <div
            className={`pulse-container${isPulseVisible ? ' visible' : ''}`}
            data-testid="pulse-panel"
            role="region"
            aria-label="The Pulse"
            aria-live="polite"
          >
            <div className="pulse-label">The Field Speaks</div>
            <p className="pulse-text" data-testid="pulse-text">
              {isPulseLoading ? 'the field is synthesizing...' : pulse}
            </p>
          </div>

          {/* Recent blessings */}
          <div className="recent-blessings" data-testid="recent-blessings-list">
            {recentBlessings.map((b, i) => (
              <div
                key={b + i}
                className="blessing-float"
                style={{ animationDelay: `${i * 3}s` }}
                data-testid="recent-blessing-item"
              >
                {b}
              </div>
            ))}
          </div>

          {/* Inkwell input */}
          <div className="inkwell" data-testid="inkwell-section">
            <p className="inkwell-label" data-testid="instruction-text">
              drop a blessing into the inkwell, if you please
            </p>
            <div className="inkwell-bowl">
              <div className="ink-surface" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="inkwell-input"
                placeholder="words for the human family..."
                value={blessingText}
                onChange={(e) => setBlessingText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSubmitting}
                maxLength={200}
                data-testid="inkwell-input"
                aria-label="Blessing input"
              />
              <button
                className="inkwell-send"
                onClick={sendBlessing}
                disabled={isSubmitting || !blessingText.trim()}
                data-testid="drop-blessing-button"
                aria-label="Drop blessing"
              >
                {/* Upward arrow */}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 12V2M7 2L3 6M7 2L11 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* Feedback */}
            <div className="blessing-feedback" aria-live="polite">
              {feedbackMsg && (
                <span key={feedbackMsg}>{feedbackMsg}</span>
              )}
            </div>

            {/* Pulse trigger */}
            <button
              className={`pulse-trigger${showPulseTrigger ? ' visible' : ''}`}
              onClick={generatePulse}
              disabled={isPulseLoading}
              data-testid="reveal-pulse-button"
              aria-label="Reveal the pulse"
            >
              {isPulseLoading ? 'the field is synthesizing...' : 'reveal the pulse'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="footer">
          <p className="footer-tagline">a guild of family guilds — building systems that meet our needs</p>
          <div className="footer-attr">
            <a href="https://perplexity.ai" target="_blank" rel="noopener noreferrer">
              Created with Perplexity Computer
            </a>
          </div>
        </footer>
      </div>

      <Toaster position="bottom-center" />
    </div>
  );
}
